import { AnimatePresence, motion as m } from 'framer-motion'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import agencySvg from '@/assets/icons/agency.svg'
import settiSvg from '@/assets/icons/setti.svg'
import { useGetCompanyByAlphaId } from '@/shared/hooks/queries/useGetCompanyByAlphaId'

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../ui/dropdown-menu'
import { CollapsibleItem } from './collapsible-item'
import { DropdownMenuTrigger } from './dropdown-menu-trigger'

interface AgencyMenuOptionProps {
  agencyId: string
  opened: boolean
}

export function AgencyMenuOption({ agencyId, opened }: AgencyMenuOptionProps) {
  const { t } = useTranslation('sidebar')

  const { posId } = useParams()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { data: agency } = useGetCompanyByAlphaId(agencyId)

  const shouldBeOpened = posId === undefined

  const [openMenu, setOpenMenu] = useState(shouldBeOpened)

  useEffect(() => {
    const menuOptionsHref = [
      `/agency/${agencyId}`,
      `/agency/${agencyId}/config`,
      `/agency/${agencyId}/details`,
      `/agency/${agencyId}/policies`,
    ]

    if (pathname.includes('point-of-sales')) {
      setOpenMenu(false)
    } else if (
      menuOptionsHref.includes(pathname) ||
      pathname.startsWith(`/agency/${agencyId}/`)
    ) {
      setOpenMenu(true)
    } else {
      setOpenMenu(false)
    }
  }, [pathname, agencyId])

  return (
    <>
      <AnimatePresence>
        {opened && (
          <Collapsible
            defaultOpen={shouldBeOpened}
            open={openMenu}
            onOpenChange={setOpenMenu}
            className="group"
          >
            <CollapsibleTrigger className="bg-[#3E54C0] enabled:hover:brightness-110 h-10 rounded-md duration-200 w-full px-2 flex items-center gap-2 mb-0 data-[state=open]:brightness-110">
              <img className="size-6" src={agencySvg} alt="Agency" />

              <AnimatePresence>
                {opened && (
                  <m.span
                    initial={{ y: 20, opacity: 0 }}
                    exit={{
                      y: 20,
                      opacity: 0,
                      transition: { duration: 0.2 },
                    }}
                    animate={{
                      y: 0,
                      opacity: 1,
                      transition: { duration: 0.2, delay: 0.2 },
                    }}
                    className="flex-1 text-left line-clamp-1"
                  >
                    {agency?.legalData.name ?? 'Carregando...'}
                  </m.span>
                )}
              </AnimatePresence>

              <img
                className="size-2 rotate-90 group-data-[state=open]:rotate-[270deg] duration-300"
                src={settiSvg}
                alt="Setti"
              />
            </CollapsibleTrigger>

            <CollapsibleContent className="mt-2 text-base">
              <CollapsibleItem to={`/agency/${agencyId}/config`}>
                {t('--menu-options-configurations')}
              </CollapsibleItem>

              <CollapsibleItem to={`/agency/${agencyId}/details`}>
                {t('--menu-options-details')}
              </CollapsibleItem>

              <CollapsibleItem
                to={`/agency/${agencyId}/policies`}
                isActive={pathname.includes(`/agency/${agencyId}/policies`)}
              >
                {t('--menu-options-purchase-policies')}
              </CollapsibleItem>

              <CollapsibleItem to={`/agency/${agencyId}`}>
                {t('--menu-options-point-of-sales')}
              </CollapsibleItem>
            </CollapsibleContent>
          </Collapsible>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {!opened && (
          <DropdownMenu>
            <DropdownMenuTrigger>
              <img className="size-6" src={agencySvg} alt="Agency" />
            </DropdownMenuTrigger>

            <DropdownMenuContent
              side="right"
              sideOffset={24}
              align="start"
              className="bg-primaryDark border-none p-2 rounded-[5px]"
            >
              <DropdownMenuItem
                onClick={() => navigate(`/agency/${agencyId}`)}
                className="px-4 text-white hover:bg-white/10 rounded-md"
              >
                {t('--menu-options-point-of-sales')}
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => navigate(`/agency/${agencyId}/policies`)}
                className="px-4 text-white hover:bg-white/10 rounded-md"
              >
                {t('--menu-options-purchase-policies')}
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => navigate(`/agency/${agencyId}/details`)}
                className="px-4 text-white hover:bg-white/10 rounded-md"
              >
                {t('--menu-options-details')}
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => navigate(`/agency/${agencyId}/config`)}
                className="px-4 text-white hover:bg-white/10 rounded-md"
              >
                {t('--menu-options-configurations')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </AnimatePresence>
    </>
  )
}
